import { useState } from 'preact/hooks';
import { SearchXIcon, TriangleAlertIcon } from 'lucide-react';
import { Alert } from '@/flows/marketplace/components/alert.js';
import { AppShell } from '@/flows/marketplace/components/app-shell.js';
import { BackLink } from '@/flows/marketplace/components/back-link.js';
import { Button } from '@/components/button.js';
import { Card } from '@/flows/marketplace/components/card.js';
import { OrderSummary } from '@/flows/marketplace/components/order-summary.js';
import { PageHero } from '@/flows/marketplace/components/page-hero.js';
import { ProgressBar } from '@/flows/marketplace/components/progress-bar.js';
import { TierCard } from '@/flows/marketplace/components/tier-card.js';
import { groupByProduct } from '@/lib/group-rated-products.js';
import { dealerContactLink } from '@/state/config.js';
import {
  cartItemCount,
  ratedProducts,
  resetRatingSession,
  unavailableProviders,
} from '@/state/session.js';
import { DealerContactLink } from '@/flows/marketplace/components/dealer-contact-link.js';
import { openPaymentFor, step } from '@/flows/marketplace/state/step.js';
import { useRerate } from '@/flows/marketplace/use-rerate.js';

export function Tiers() {
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  // A retry can retire the tier the open card was showing, so collapse on success.
  const rerate = useRerate(() => setExpandedProductId(null));

  const grouped = groupByProduct(ratedProducts.value);
  const contact = dealerContactLink.value;
  const hasUnavailable = unavailableProviders.value.length > 0;

  function handleBack() {
    resetRatingSession();
    step.value = 'entry';
  }

  function handleContinue() {
    step.value = 'customer-info';
  }

  const retryButton = (
    <button
      type="button"
      className="sm-type-body sm-link"
      onClick={rerate.run}
      disabled={rerate.pending}
    >
      {rerate.pending ? 'Retrying…' : 'Retry'}
    </button>
  );

  // A total provider outage comes back as a *successful* rate response with an
  // empty `rated_products` and a populated `unavailable_providers`. That must not
  // read as "this vehicle has no plans" — it's a temporary failure, and the
  // customer needs the retry more here than in the partial case.
  const isOutage = grouped.length === 0 && hasUnavailable;

  // The retry button sits right below, so the fallback doesn't repeat the nudge.
  const outageText = rerate.error?.message ?? "Our providers aren't responding right now.";

  return (
    <AppShell>
      <BackLink onClick={handleBack} />

      <PageHero
        title="Compare your plans"
        subtitle={
          cartItemCount.value === 0
            ? "Tap a plan to see its terms and price. You'll choose how to pay for each plan you select."
            : "Add another plan, or continue to checkout when you're done."
        }
      />

      <ProgressBar />

      <Card>
        {hasUnavailable && grouped.length > 0 && (
          <Alert
            role="status"
            copy={{
              title: "Some plans aren't available right now",
              message: rerate.error?.message ?? 'We loaded what we could.',
            }}
          >
            {retryButton}
          </Alert>
        )}

        {grouped.length === 0 ? (
          <div className="sm-stack sm-empty-state">
            <span className="sm-center sm-empty-disc">
              {isOutage ? (
                <TriangleAlertIcon size={24} aria-hidden="true" />
              ) : (
                <SearchXIcon size={24} aria-hidden="true" />
              )}
            </span>
            <div className="sm-stack sm-empty-text-block">
              <h2 className="sm-type-heading">
                {isOutage ? "We couldn't load your plans" : 'No plans available for this vehicle'}
              </h2>
              <p className="sm-type-body sm-muted">
                {isOutage
                  ? outageText
                  : contact !== null
                    ? 'Try a different vehicle, or reach out for help.'
                    : 'Try a different vehicle, or contact your dealer.'}
              </p>
            </div>
            {isOutage ? retryButton : <DealerContactLink />}
          </div>
        ) : (
          <>
            <div className="sm-stack sm-products">
              {grouped.map((group) => (
                <TierCard
                  key={group.productId}
                  quoteItemIds={group.quoteItemIds}
                  expanded={expandedProductId === group.productId}
                  onToggleExpand={() =>
                    setExpandedProductId((prev) =>
                      prev === group.productId ? null : group.productId,
                    )
                  }
                  onSelected={(cartIndex) => openPaymentFor(cartIndex, 'tiers')}
                />
              ))}
            </div>

            {/* Only an exit: selecting a plan moves the customer on by itself. */}
            {cartItemCount.value > 0 && (
              <div className="sm-product-actions">
                <Button onClick={handleContinue} variant="secondary" endIcon={null}>
                  Continue to checkout
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {cartItemCount.value > 0 && <OrderSummary />}
    </AppShell>
  );
}
